import { AlertTriangle, ArrowRight, LogOut, ShieldCheck, Wallet } from 'lucide-react'
import { useAccount, useDisconnect } from 'wagmi'
import { shortChainName } from '@/lib/wagmi'
import { signInFailureReason } from '@/hooks/useAuth'
import { switchErrorMessage, useWalletGate } from '@/lib/wallet-gate'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/Button'
import { Badge } from '@/ui/primitives'

/**
 * Header wallet control.
 *
 * All state comes from `useWalletGate()` — the same source the perps order ticket
 * reads. This component decides only how to PRESENT each state, never what the
 * state is. That separation is what stops the header and the ticket disagreeing
 * about whether you can trade.
 */
export function ConnectButton() {
  const {
    state,
    address,
    requiredChain,
    openPicker,
    switchToRequired,
    switchError,
    isBusy,
    signIn,
    signOut,
    canSignIn,
    signInError,
    signingIn,
  } = useWalletGate()
  const { isConnecting, chainId } = useAccount()
  const { disconnect } = useDisconnect()
  const switching = isBusy && state === 'wrong-chain'

  switch (state) {
    // wagmi rehydrates a stored connection on mount. Rendering "Connect wallet"
    // during that window makes every reload flash a disconnected state and invites
    // the user into a flow that is about to resolve on its own.
    case 'reconnecting':
      return (
        <Button variant="secondary" size="sm" loading disabled aria-label="Reconnecting" className="max-sm:size-10 max-sm:px-0">
          <span className="max-sm:hidden">Reconnecting</span>
        </Button>
      )

    case 'disconnected':
      return (
        <Button
          onClick={openPicker}
          loading={isConnecting}
          iconLeft={<Wallet className="size-4" />}
          aria-label="Connect wallet"
          className="max-sm:size-10 max-sm:px-0"
        >
          <span className="max-sm:hidden">Connect wallet</span>
        </Button>
      )

    // Connected with no account exposed — a locked wallet, or one whose permission
    // for this site was revoked. Without this branch the UI renders `undefined`
    // into the address chip and account-scoped queries fire for "undefined".
    case 'locked':
      return (
        <div className="flex items-center gap-1 sm:gap-2">
          <Badge tone="warning">Wallet locked</Badge>
          <Button variant="ghost" size="sm" onClick={() => disconnect()} aria-label="Disconnect">
            <LogOut className="size-4" />
          </Button>
        </div>
      )

    /*
     * Wrong network.
     *
     * This was a full-width filled red button reading "Switch to BNB Smart Chain
     * Testnet" — the loudest, widest element on the page, for a one-click fix that
     * is inconvenient rather than dangerous. Filled red is the vocabulary of
     * destructive and irreversible; a network switch is neither.
     *
     * Now: a compact chip in warning tone showing the CURRENT network, with the
     * target beside it. It reads as "you are here, go there" instead of shouting.
     */
    case 'wrong-chain': {
      // Unreachable without a requirement (see the gate), but narrow it properly
      // rather than reaching for `!` — Swap legitimately has none.
      if (!requiredChain) return null
      return (
        <div className="flex items-center gap-1 sm:gap-2">
          {/* A failed switch is the whole reason the user is still on this branch,
              so the reason is never hidden behind a breakpoint. */}
          {switchError && (
            <span className="max-w-[10rem] truncate text-xs text-danger sm:max-w-[16rem]">
              {switchErrorMessage(switchError, shortChainName(requiredChain.id))}
            </span>
          )}
          <button
            onClick={switchToRequired}
            disabled={switching}
            title={
              switchError
                ? switchErrorMessage(switchError, shortChainName(requiredChain.id))
                : `Switch from ${chainId ? shortChainName(chainId) : 'this network'} to ${requiredChain.name}`
            }
            className="inline-flex items-center gap-2 rounded-full bg-warning/10 py-1 pl-2.5 pr-1.5 text-xs font-medium text-warning ring-1 ring-warning/30 transition-colors hover:bg-warning/15 disabled:opacity-60"
          >
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
            <span className="hidden sm:inline">
              {chainId ? shortChainName(chainId) : 'Wrong network'}
            </span>
            <ArrowRight className="size-3 shrink-0 opacity-70" aria-hidden />
            <span className="rounded-full bg-warning/20 px-2 py-0.5 text-warning">
              {switching ? 'Switching…' : shortChainName(requiredChain.id)}
            </span>
          </button>
          <Button variant="ghost" size="sm" onClick={() => disconnect()} aria-label="Disconnect">
            <LogOut className="size-4" />
          </Button>
        </div>
      )
    }

    /*
     * Connected, unlocked, on the right chain — everything on-chain works from here.
     *
     * A SIWE session is NOT required to reach this state, so it is offered rather than
     * demanded. This once replaced the whole control with a "Sign in" button, which
     * meant a user with a connected wallet could not see their own address whenever
     * the API was unreachable.
     *
     * ── Why sign-in lives INSIDE the address chip ──
     * The fix above overcorrected: a bare "Sign in" sat as loose text between the
     * network chip and the address chip, so the header read as two competing answers
     * to "am I connected?" — an address that says yes and a sign-in prompt that looks
     * like no. They are one subject, so they are one control. Signed out, the chip is
     * a button whose label carries the offer; signed in, it is a static chip with a
     * verified mark. Either way there is exactly one thing in the header representing
     * the wallet.
     */
    case 'ready': {
      const short = address ? `${address.slice(0, 6)}…${address.slice(-4)}` : ''
      return (
        <div className="flex items-center gap-1 sm:gap-2">
          {/*
           * Show the network the wallet is on. This used to appear only on routes
           * with no chain requirement, on the theory that a required chain is
           * implied — but "which network am I on" is the question users ask right
           * before they transact, and the answer was hidden precisely there.
           */}
          {chainId && (
            // A full network chip competes with the wallet address for room. 380px was too
            // optimistic: at 390px (a standard phone) the chip, the address, sign-out and the
            // theme toggle together overflow the bar and push the whole page sideways. The
            // connection state stays visible through the green verified badge, so the verbose
            // network name waits for a tablet-width viewport.
            <div className="hidden shrink-0 sm:block">
              <Badge tone="neutral">
                <span className="size-1.5 rounded-full bg-positive" aria-hidden />
                {shortChainName(chainId)}
              </Badge>
            </div>
          )}

          {canSignIn ? (
            <button
              type="button"
              onClick={signIn}
              disabled={signingIn}
              title={
                signInError
                  ? signInFailureReason(signInError)
                  : 'Sign a message to prove you own this address. Everything on-chain already works without it.'
              }
              className={cn(
                'group inline-flex items-center gap-2 rounded-full bg-elevated py-1 pl-2.5 pr-1 text-xs ring-1 transition-colors',
                signInError
                  ? 'ring-danger/40 hover:ring-danger/60'
                  : 'ring-line hover:ring-accent/50',
              )}
            >
              <span className="tnum truncate text-muted">{short}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors max-sm:hidden',
                  signInError
                    ? 'bg-danger/15 text-danger'
                    : 'bg-accent/15 text-accent group-hover:bg-accent/25',
                )}
              >
                {signingIn ? 'Signing…' : signInError ? 'Retry' : 'Sign in'}
              </span>
            </button>
          ) : (
            <Badge tone="accent" className="max-sm:px-2">
              <ShieldCheck className="size-3" aria-hidden />
              <span className="tnum">{short}</span>
            </Badge>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="max-sm:size-10 max-sm:px-0"
            onClick={() => {
              signOut()
              disconnect()
            }}
            aria-label="Disconnect wallet"
          >
            <LogOut className="size-4" />
          </Button>
        </div>
      )
    }
  }
}
